/**
 * ADR-0214 Part 2 revision 3 — one Activity card's detail screen (screen spec
 * §3B.7). The same shape for all nine cards: a breadcrumb back to Activity,
 * the title and "Ranked by … · <period> · business work", then the full ranked
 * list with the same bars and the counts in plain columns. Clicking a row
 * opens its panel below it (the first row is open to begin with): the reason
 * in words, then the rows behind it. A person opens their person page and a
 * subject its subject page. Never query text or SQL; never an id.
 */

import { Fragment, useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PageLayout } from "../components/PageLayout.js";
import { TrailLinks, useActivityTrail } from "../components/ActivityTrail.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { apiFetch } from "../lib/api.js";
import { when } from "../lib/subjects.js";
import type { TrailState } from "../lib/activityTrail.js";
import { AUDIENCE_LABEL, cardCarried, periodLabel, workLabel, type ActivityCardResponse, type Cell, type Column } from "../lib/activity.js";
import { ActivityToolbar, NameBar, useActivityRange } from "./TenantActivity.js";

function CellView(props: { cell: Cell; type: Column["type"]; timeZone: string; range: string; onward: TrailState }): ReactNode {
  const c = props.cell;
  if ("count" in c) return c.count.toLocaleString();
  if ("at" in c) return when(c.at, props.timeZone);
  if ("text" in c) return c.text;
  if ("person" in c) {
    return c.key ? (
      <Link to={`/app/subjects/people/${c.key}?${props.range}`} state={props.onward}>
        {c.person}
      </Link>
    ) : (
      <span className="muted">{c.person}</span>
    );
  }
  if (c.subjects.length === 0) return "—";
  return (
    <>
      {c.subjects.map((s, i) => (
        <Fragment key={s.number}>
          {i > 0 ? ", " : null}
          <Link to={`/app/subjects/${s.number}?${props.range}`} state={props.onward}>
            {s.title}
          </Link>
        </Fragment>
      ))}
      {c.more > 0 ? <span className="muted"> and {c.more.toLocaleString()} more</span> : null}
    </>
  );
}

const numeric = (t: Column["type"]) => (t === "count" ? { textAlign: "right" as const } : {});

export function TenantActivityCard() {
  const { card } = useParams();
  const range = useActivityRange();
  const navigate = useNavigate();
  const [data, setData] = useState<ActivityCardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<number | null>(0);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await apiFetch<ActivityCardResponse>(`/api/tenant/activity/cards/${encodeURIComponent(card ?? "")}?${range.query}`));
      setOpen(0);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      // §3B.6: Everyone does not carry the cards that name people. An address that asks for
      // one (a pasted link, say) opens Activity under Everyone rather than a dead end.
      if (message === "not_available" && !cardCarried(card, range.audience)) {
        navigate(`/app/activity?${range.query}`, { replace: true });
        return;
      }
      setError(message);
    }
  }, [card, range.query, range.audience, navigate]);

  // Picking an audience that does not carry this card goes to Activity under that audience.
  const change = (next: Record<string, string>) => {
    if (next.audience !== undefined && !cardCarried(card, next.audience)) {
      navigate(`/app/activity?${range.queryWith(next)}`);
      return;
    }
    range.set(next);
  };

  useEffect(() => {
    void load();
  }, [load]);

  // C7: the trail walked to this card (else Activity, as it is now), then this card.
  const trail = useActivityTrail(data?.card.title ?? "Activity", [{ to: `/app/activity?${range.query}`, label: "Activity" }]);
  const crumbs = (title: string) => (
    <>
      <TrailLinks trail={trail.above} /> › {title}
    </>
  );
  const toolbar = <ActivityToolbar from={range.from} to={range.to} scope={data} onChange={change} />;

  if (error) {
    return (
      <PageLayout title="Activity" breadcrumb={crumbs("Not available")}>
        <div style={{ display: "grid", gap: 12 }}>
          <ErrorState detail={error === "not_available" ? "There is no such card." : error} />
        </div>
      </PageLayout>
    );
  }
  if (!data) {
    return (
      <PageLayout title="Activity">
        <div style={{ display: "grid", gap: 12 }}>
          {toolbar}
          <LoadingState />
        </div>
      </PageLayout>
    );
  }

  const tz = data.time_zone;
  const head = data.card;
  const top = data.rows[0]?.value ?? 0;
  const nameHeader = head.key === "users" || head.key === "quiet" ? "Person" : "Name";

  return (
    <PageLayout title={head.title} breadcrumb={crumbs(head.title)} description={`${head.by} · ${periodLabel(range.from, range.to)} · ${AUDIENCE_LABEL[data.audience]} · ${workLabel(data.purpose)}`}>
      <div style={{ display: "grid", gap: 12 }}>
        {toolbar}
        {head.note ? <div className="muted">{head.note}</div> : null}
        {head.key === "quiet" ? (
          // "Gone quiet after N days" lives here, not on the toolbar (owner, 2026-10-19); the dashboard card uses 7 or the address's value.
          <label className="field" style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span className="label">No activity for</span>
            <input
              className="input"
              type="number"
              min={1}
              max={365}
              value={range.quietDays}
              style={{ width: 70 }}
              onChange={(e) => range.set({ quiet_days: e.target.value || "7" })}
              aria-label="Days with no activity before a person counts as gone quiet"
            />
            <span className="label">days</span>
          </label>
        ) : null}

        {data.rows.length === 0 ? (
          <div className="card muted" style={{ padding: 12 }}>
            {head.empty}
          </div>
        ) : (
          <div className="card adminList">
            <div className="tableWrap tableWrapFrame">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: "40%" }}>{nameHeader}</th>
                    {data.columns.map((col) => (
                      <th key={col.label} style={numeric(col.type)}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <Fragment key={`${r.kind ?? ""}:${r.name}:${i}`}>
                      <tr
                        onClick={() => setOpen(open === i ? null : i)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setOpen(open === i ? null : i);
                          }
                        }}
                        tabIndex={0}
                        aria-expanded={open === i}
                        style={{ cursor: "pointer", background: open === i ? "var(--bg)" : undefined }}
                      >
                        <td>
                          <NameBar name={r.name} value={r.value} top={top} tone={head.tone} />
                        </td>
                        {r.cells.map((cell, j) => (
                          <td key={j} style={numeric(data.columns[j]!.type)}>
                            <CellView cell={cell} type={data.columns[j]!.type} timeZone={tz} range={range.query} onward={trail.onward} />
                          </td>
                        ))}
                      </tr>
                      {open === i ? (
                        <tr>
                          <td colSpan={data.columns.length + 1} style={{ background: "var(--bg)" }}>
                            <div style={{ display: "grid", gap: 8, padding: "4px 0 8px" }}>
                              <div>
                                {r.panel.reason}
                                {r.panel.person ? (
                                  <>
                                    {" "}
                                    <Link to={`/app/subjects/people/${r.panel.person.key}?${range.query}`} state={trail.onward}>
                                      Open {r.panel.person.person}’s page
                                    </Link>
                                  </>
                                ) : null}
                              </div>
                              {r.panel.rows.length === 0 ? (
                                <div className="muted">Nothing more recorded.</div>
                              ) : (
                                <table className="table">
                                  <thead>
                                    <tr>
                                      {r.panel.columns.map((col) => (
                                        <th key={col.label} style={numeric(col.type)}>
                                          {col.label}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {r.panel.rows.map((row, k) => (
                                      <tr key={k}>
                                        {row.map((cell, j) => (
                                          <td key={j} style={numeric(r.panel.columns[j]!.type)}>
                                            <CellView cell={cell} type={r.panel.columns[j]!.type} timeZone={tz} range={range.query} onward={trail.onward} />
                                          </td>
                                        ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                              {r.panel.more > 0 ? <div className="muted">and {r.panel.more.toLocaleString()} more</div> : null}
                              {(r.panel.sections ?? []).map((sec) => (
                                <div key={sec.title} style={{ display: "grid", gap: 4, marginTop: 6 }}>
                                  <div style={{ fontWeight: 700 }}>{sec.title}</div>
                                  {sec.rows.length === 0 ? (
                                    <div className="muted">{sec.empty}</div>
                                  ) : (
                                    <table className="table">
                                      <thead>
                                        <tr>
                                          {sec.columns.map((col) => (
                                            <th key={col.label} style={numeric(col.type)}>
                                              {col.label}
                                            </th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {sec.rows.map((row, k) => (
                                          <tr key={k}>
                                            {row.map((cell, j) => (
                                              <td key={j} style={numeric(sec.columns[j]!.type)}>
                                                <CellView cell={cell} type={sec.columns[j]!.type} timeZone={tz} range={range.query} onward={trail.onward} />
                                              </td>
                                            ))}
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}
                                  {sec.more > 0 ? <div className="muted">and {sec.more.toLocaleString()} more</div> : null}
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {data.truncated ? <div className="muted">Showing the top {data.rows.length.toLocaleString()}.</div> : null}
      </div>
    </PageLayout>
  );
}
